import type React from 'react';
import { useState } from 'react';
import { Table, Badge, Text, Paper, Stack, Group, Tooltip, ScrollArea, Collapse, ActionIcon } from '@mantine/core';
import { IconCrown, IconInfoCircle, IconArrowRight, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { TariffComparisonDetail } from './TariffComparisonDetail';
import type { CalculationResults, TariffResult, TariffType } from '../../../../types/calculator';
import { MONTH_NAMES_PL } from '../../../../types/calculator';
import { getTariffBadge } from '../../../../utils/tariffMetadata';
import { TariffType as TariffTypeEnum } from '../../../../types/graphql';
// OSD Metadata for dynamic messages
import { getOSDShortName, type OSDCode } from '../../../../utils/osdMetadata';

/**
 * Results Table Component Props
 */
interface ResultsTableProps {
  results: CalculationResults;
  currentTariff?: TariffType;
  baselineTariff?: TariffType;           // NEW: Tariff used as baseline for comparison
  onBaselineTariffChange?: (tariff: TariffType) => void;  // NEW: Callback for baseline change
  /** Selected OSD for dynamic messages */
  selectedOSD?: OSDCode;
  /** Calculation year for dynamic messages */
  calculationYear?: number;
}

/**
 * Tariff Info Tooltip
 * Shows additional information for special tariffs
 */
function TariffInfoIcon({ tariff }: { tariff: string }) {
  const getTooltipText = () => {
    switch (tariff) {
      case 'G13s':
        return 'Wymaga inteligentnego licznika i umożliwia wykorzystanie energii OZE';
      case 'G12as':
        return 'Taryfa antysmogowa z korzystniejszymi cenami w okresie grzewczym';
      case 'G12w':
        return 'Taryfa weekendowa z niższymi cenami w weekendy';
      case 'G13':
        return 'Taryfa trzystrefowa dostępna tylko u operatora Tauron';
      case 'G12n':
        return 'Taryfa nocna PGE - tania noc 01:00-05:00 w dni robocze + całe weekendy';
      case 'G12r':
        return 'Taryfa rozszerzona weekendowa Energa - dłuższa noc 21:00-07:00 + weekendy';
      default:
        return null;
    }
  };

  const tooltipText = getTooltipText();
  if (!tooltipText) {return null;}

  return (
    <Tooltip label={tooltipText} withArrow>
      <IconInfoCircle size={16} style={{ marginLeft: 4, opacity: 0.6 }} />
    </Tooltip>
  );
}

/**
 * OSD Badge Component
 * Shows OSD-specific badge for tariffs like G12n (PGE) and G12r (ENERGA)
 */
function OsdBadge({ tariff }: { tariff: TariffType }) {
  const badge = getTariffBadge(tariff as TariffTypeEnum);
  if (!badge || !['PGE', 'ENERGA'].includes(badge)) {return null;}

  const color = badge === 'PGE' ? 'orange' : 'grape';
  return (
    <Badge size="xs" color={color} variant="light">
      {badge}
    </Badge>
  );
}

/**
 * Expandable Cost Breakdown Component
 * Shows monthly breakdown of energy sales and distribution costs
 */
interface CostBreakdownProps {
  energyCost: number;
  distributionCost: number;
  totalCost: number;
}

function ExpandableCostBreakdown({ energyCost, distributionCost, totalCost }: CostBreakdownProps) {
  const [openEnergy, setOpenEnergy] = useState(false);
  const [openDistribution, setOpenDistribution] = useState(false);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Calculate monthly breakdown (simplified - dividing annual by 12)
  const monthlyEnergy = energyCost / 12;
  const monthlyDistribution = distributionCost / 12;

  return (
    <Stack gap={8}>
      {/* Energy Cost Section */}
      <div>
        <Group gap="xs" wrap="nowrap">
          <ActionIcon
            variant="subtle"
            size="xs"
            onClick={(e) => {
              e.stopPropagation();
              setOpenEnergy(!openEnergy);
            }}
          >
            {openEnergy ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
          </ActionIcon>
          <Text size="sm" fw={500}>
            <Text component="span" c="dimmed">Sprzedaż energii:</Text> {formatCurrency(energyCost)}
          </Text>
        </Group>
        <Collapse in={openEnergy}>
          <Paper p="xs" mt="xs" ml={24} withBorder>
            <Stack gap={4}>
              <Text size="xs" fw={600} c="dimmed">Miesięczny rozkład (szacunkowy):</Text>
              {Object.entries(MONTH_NAMES_PL).map(([monthNum, monthName]) => (
                <Group key={monthNum} justify="space-between">
                  <Text size="xs">{monthName}</Text>
                  <Text size="xs" fw={500}>{formatCurrency(monthlyEnergy)}</Text>
                </Group>
              ))}
              <Group justify="space-between" pt="xs" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
                <Text size="xs" fw={600}>Suma roczna:</Text>
                <Text size="xs" fw={600}>{formatCurrency(energyCost)}</Text>
              </Group>
            </Stack>
          </Paper>
        </Collapse>
      </div>

      {/* Distribution Cost Section */}
      <div>
        <Group gap="xs" wrap="nowrap">
          <ActionIcon
            variant="subtle"
            size="xs"
            onClick={(e) => {
              e.stopPropagation();
              setOpenDistribution(!openDistribution);
            }}
          >
            {openDistribution ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
          </ActionIcon>
          <Text size="sm" fw={500}>
            <Text component="span" c="dimmed">Dystrybucja:</Text> {formatCurrency(distributionCost)}
          </Text>
        </Group>
        <Collapse in={openDistribution}>
          <Paper p="xs" mt="xs" ml={24} withBorder>
            <Stack gap={4}>
              <Text size="xs" fw={600} c="dimmed">Miesięczny rozkład (szacunkowy):</Text>
              {Object.entries(MONTH_NAMES_PL).map(([monthNum, monthName]) => (
                <Group key={monthNum} justify="space-between">
                  <Text size="xs">{monthName}</Text>
                  <Text size="xs" fw={500}>{formatCurrency(monthlyDistribution)}</Text>
                </Group>
              ))}
              <Group justify="space-between" pt="xs" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
                <Text size="xs" fw={600}>Suma roczna:</Text>
                <Text size="xs" fw={600}>{formatCurrency(distributionCost)}</Text>
              </Group>
            </Stack>
          </Paper>
        </Collapse>
      </div>

      {/* Total */}
      <Text size="sm" fw={600}>
        <Text component="span" c="dimmed">Suma:</Text> {formatCurrency(totalCost)}
      </Text>
    </Stack>
  );
}

/**
 * ResultsTable Component
 *
 * Updated: Interactive comparison table for 2-6 tariffs
 * Displays:
 * - Tariff name with recommended badge and info tooltips
 * - Energy production cost (PLN/year)
 * - Distribution cost (PLN/year)
 * - Total annual cost (PLN/year)
 * - Savings/Loss compared to baseline tariff (interactive)
 * - Average cost per kWh
 *
 * Features:
 * - Click any tariff row to make it the baseline for comparison
 * - Savings/loss recalculates dynamically based on selected baseline
 *
 * @param results - Calculation results from backend
 * @param currentTariff - User's current tariff for comparison
 * @param baselineTariff - Tariff used as baseline for savings calculation
 * @param onBaselineTariffChange - Callback when user clicks a tariff row
 */
export function ResultsTable({
  results,
  currentTariff,
  baselineTariff,
  onBaselineTariffChange,
  selectedOSD = 'TAURON',
  calculationYear = 2026,
}: ResultsTableProps) {
  // Helper function to format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Helper function to format savings/loss with monthly and annual breakdown
  const formatSavingsLoss = (currentCost: number, baselineCost: number): React.ReactElement => {
    const differenceAnnual = baselineCost - currentCost;
    const differenceMonthly = differenceAnnual / 12;

    const isGain = differenceAnnual > 0;
    const isLoss = differenceAnnual < 0;
    const isZero = Math.abs(differenceAnnual) < 0.01; // tolerance for floating point
    const color = isGain ? 'green.6' : isLoss ? 'red.6' : 'gray.6';
    const label = isGain ? 'Zysk' : 'Strata';

    return (
      <Stack gap={4}>
        <Text c={color} fw={600} size="sm">
          {!isZero && `${label}: `}{Math.abs(differenceMonthly).toFixed(2)} zł miesięcznie
        </Text>
        <Text c={color} fw={600} size="sm">
          {!isZero && `${label}: `}{Math.abs(differenceAnnual).toFixed(2)} zł rocznie
        </Text>
      </Stack>
    );
  };

  // Helper function to calculate percentage difference
  const formatPercentageDifference = (currentCost: number, baselineCost: number): React.ReactElement => {
    if (baselineCost === 0) {
      return <Text size="sm" c="gray.6">0%</Text>;
    }

    const percentageDiff = ((baselineCost - currentCost) / baselineCost) * 100;
    const isGain = percentageDiff > 0;
    const isLoss = percentageDiff < 0;
    const color = isGain ? 'green.6' : isLoss ? 'red.6' : 'gray.6';

    return (
      <Text c={color} fw={600} size="sm">
        {percentageDiff > 0 ? '+' : ''}{percentageDiff.toFixed(1)}%
      </Text>
    );
  };

  // Extract all tariffs that have results (dynamic 2-6 tariffs)
  const availableTariffs: Array<{ code: TariffType; result: TariffResult }> = [];

  const tariffMapping: Array<{ code: TariffType; key: keyof CalculationResults }> = [
    { code: 'G11', key: 'g11' },
    { code: 'G12', key: 'g12' },
    { code: 'G12as', key: 'g12as' },
    { code: 'G12w', key: 'g12w' },
    { code: 'G12n', key: 'g12n' },   // PGE-specific: Nocna
    { code: 'G12r', key: 'g12r' },   // Energa-specific: Rozszerzona weekendowa
    { code: 'G13', key: 'g13' },
    { code: 'G13s', key: 'g13s' }
  ];

  tariffMapping.forEach(({ code, key }) => {
    const result = results[key];
    if (result && typeof result === 'object' && 'tariffName' in result) {
      availableTariffs.push({ code, result });
    }
  });

  // Get baseline tariff for row highlighting (clicked row)
  const effectiveBaseline = baselineTariff || currentTariff || 'G11';

  // Get current tariff cost (always use for savings/loss calculation)
  const currentTariffData = availableTariffs.find(t => t.code === currentTariff);
  const currentTariffCost = currentTariffData?.result.totalAnnualCost || 0;

  // Build table rows for all available tariffs
  const rows = availableTariffs.map(({ code, result: tariff }) => {
    const isBaseline = code === effectiveBaseline;

    return (
      <Table.Tr
        key={tariff.tariffName}
        onClick={() => onBaselineTariffChange?.(code)}
        style={{
          backgroundColor: isBaseline
            ? 'var(--mantine-color-blue-0)'
            : tariff.isRecommended
            ? 'var(--mantine-color-green-0)'
            : undefined,
          cursor: onBaselineTariffChange ? 'pointer' : 'default',
          transition: 'background-color 0.2s ease'
        }}
      >
        {/* Tariff Name - Format: "Current → New" */}
        <Table.Td>
          <Group gap="xs" wrap="nowrap">
            {isBaseline && (
              <IconArrowRight size={20} color="var(--mantine-color-blue-6)" />
            )}
            <Text
              fw={600}
              size="md"
              c={tariff.tariffName === currentTariff ? 'dimmed' : undefined}
            >
              {currentTariff} → {tariff.tariffName}
            </Text>
            <TariffInfoIcon tariff={code} />
            <OsdBadge tariff={code} />
            {tariff.tariffName === currentTariff && (
              <Badge size="xs" color="gray" variant="light">
                Bez zmiany
              </Badge>
            )}
            {tariff.isRecommended && (
              <Badge
                color="green"
                variant="filled"
                leftSection={<IconCrown size={14} />}
                size="sm"
              >
                Rekomendowane
              </Badge>
            )}
          </Group>
        </Table.Td>

        {/* Combined Annual Costs - with expandable breakdowns */}
        <Table.Td>
          <ExpandableCostBreakdown
            energyCost={tariff.energyCost}
            distributionCost={tariff.distributionCost}
            totalCost={tariff.totalAnnualCost}
          />
        </Table.Td>

        {/* Savings/Loss (compared to current tariff - ALWAYS) */}
        <Table.Td>
          {formatSavingsLoss(tariff.totalAnnualCost, currentTariffCost)}
        </Table.Td>

        {/* Percentage Difference (compared to current tariff - ALWAYS) */}
        <Table.Td>
          {formatPercentageDifference(tariff.totalAnnualCost, currentTariffCost)}
        </Table.Td>

        {/* Average Cost per kWh */}
        <Table.Td>
          <Text size="sm" c="dimmed">
            {formatCurrency(tariff.averageCostPerKwh)}/kWh
          </Text>
        </Table.Td>
      </Table.Tr>
    );
  });

  // Get baseline tariff data (clicked row) for detailed comparison
  const baselineTariffData = availableTariffs.find(t => t.code === effectiveBaseline);
  const baselineTariffCost = baselineTariffData?.result.totalAnnualCost || 0;

  // Calculate savings for detailed comparison: currentTariff vs clicked baseline
  const detailedComparisonSavings = currentTariffCost - baselineTariffCost;
  const showDetailedComparison = baselineTariff && baselineTariff !== currentTariff && Math.abs(detailedComparisonSavings) > 0.01;

  return (
    <Stack gap="md">
      {/* Comparison Table - with horizontal scroll on mobile */}
      <Paper shadow="sm" withBorder>
        <ScrollArea>
          <Table
            striped
            highlightOnHover
            withTableBorder={false}
            verticalSpacing="md"
            horizontalSpacing="lg"
            style={{ minWidth: 800 }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Zmiana taryfy</Table.Th>
                <Table.Th>Koszty roczne</Table.Th>
                <Table.Th>Oszczędność/Strata po zmianie taryfy</Table.Th>
                <Table.Th>Zmiana (%)</Table.Th>
                <Table.Th>Średnia cena za kWh</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>

      {/* Cost Breakdown Info */}
      <Paper p="md" bg="gray.0">
        <Stack gap="xs">
          <Text size="sm" fw={600}>
            Informacja o kosztach:
          </Text>
          <Text size="xs" c="dimmed">
            • <strong>Koszt energii</strong>: Cena zakupu energii elektrycznej (produkcja)
          </Text>
          <Text size="xs" c="dimmed">
            • <strong>Koszt dystrybucji</strong>: Opłata za przesył energii (sieć + opłata stała)
          </Text>
          <Text size="xs" c="dimmed">
            • Wszystkie ceny zawierają VAT 23% i opłatę jakościową
          </Text>
          <Text size="xs" c="dimmed">
            • Dane cenowe: {getOSDShortName(selectedOSD)} {calculationYear} (obowiązujące {calculationYear === 2025 ? 'do 31.12.2025' : 'od 01.01.2026'})
          </Text>
        </Stack>
      </Paper>

      {/* Detailed Tariff Comparison - currentTariff vs clicked baseline */}
      {showDetailedComparison && (
        <TariffComparisonDetail
          currentTariff={currentTariff || 'G11'}
          recommendedTariff={baselineTariff}
          savingsAmount={Math.abs(detailedComparisonSavings)}
          savingsPercent={(Math.abs(detailedComparisonSavings) / currentTariffCost) * 100}
        />
      )}
    </Stack>
  );
}
